import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UtensilsCrossed, Coffee, Package, Boxes, ShoppingCart,
  Calculator, Users, Receipt, BarChart3, Settings, LogOut, Sun, Moon,
  Menu, X, Layers, Coffee as Logo
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const NAV = [
  { id: "dashboard", path: "/", icon: LayoutDashboard },
  { id: "recipes", path: "/recipes", icon: UtensilsCrossed },
  { id: "products", path: "/products", icon: Coffee },
  { id: "ingredients", path: "/ingredients", icon: Package },
  { id: "bases", path: "/bases", icon: Layers },
  { id: "inventory", path: "/inventory", icon: Boxes },
  { id: "purchasing", path: "/purchasing", icon: ShoppingCart },
  { id: "pos", path: "/pos", icon: Calculator },
  { id: "customers", path: "/customers", icon: Users },
  { id: "finance", path: "/finance", icon: Receipt },
  { id: "reports", path: "/reports", icon: BarChart3 },
  { id: "settings", path: "/settings", icon: Settings },
];

const NAV_PERM = { products: "masters", ingredients: "masters", bases: "masters" };

const DEFAULT_PERMS = {
  owner: ["*"],
  admin: ["dashboard", "masters", "recipes", "inventory", "purchasing", "pos", "customers", "finance", "reports", "settings"],
  kasir: ["dashboard", "pos", "customers"],
};

export function Layout() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem("kqz_theme") || "light");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [outlet, setOutlet] = useState(() => localStorage.getItem("kqz_outlet") || "");
  const lockedOutlet = user && user.role !== "owner" && user.outlet_id ? user.outlet_id : null;

  useEffect(() => {
    if (lockedOutlet && outlet !== lockedOutlet) {
      setOutlet(lockedOutlet);
      localStorage.setItem("kqz_outlet", lockedOutlet);
    }
  }, [lockedOutlet]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("kqz_theme", theme);
  }, [theme]);

  useEffect(() => {
    api.get("/meta/all").then((r) => {
      setOutlets(r.data.outlets || []);
      if (localStorage.getItem("kqz_outlet") === null && r.data.outlets?.[0]) {
        setOutlet(r.data.outlets[0].id);
        localStorage.setItem("kqz_outlet", r.data.outlets[0].id);
      }
    }).catch(() => {});
  }, []);

  const perms = user?.perms?.length ? user.perms : DEFAULT_PERMS[user?.role] || DEFAULT_PERMS.kasir;
  const navItems = NAV.filter((n) => perms.includes("*") || perms.includes(NAV_PERM[n.id] || n.id));

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
        <div className="w-9 h-9 rounded-xl bg-amber-600 flex items-center justify-center shrink-0">
          <Logo className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-white text-sm leading-tight truncate">KQZ Business</div>
          <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">F&B ERP & POS</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.path === "/"}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.id}-btn`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-amber-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`
            }
          >
            <item.icon className="w-4.5 h-4.5 w-[18px] h-[18px] shrink-0" />
            <span className="truncate">{t(`nav_${item.id}`)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-3 py-2">
          <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-amber-500 font-mono uppercase">{user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-rose-400 hover:bg-slate-800 w-full transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> {t("logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#0F172A] dark:bg-[#030712] z-40">{sidebar}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0F172A]">{sidebar}</aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center gap-2 px-4 md:px-6 h-16">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <select
              data-testid="outlet-switcher"
              value={outlet}
              disabled={!!lockedOutlet}
              onChange={(e) => { setOutlet(e.target.value); localStorage.setItem("kqz_outlet", e.target.value); }}
              className="text-sm bg-muted border border-border rounded-lg px-2.5 py-1.5 max-w-[160px] md:max-w-none disabled:opacity-70"
            >
              {!lockedOutlet && <option value="">{t("allOutlets")}</option>}
              {(lockedOutlet ? outlets.filter((o) => o.id === lockedOutlet) : outlets).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <div className="flex-1" />
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white hidden sm:flex" onClick={() => navigate("/pos")} data-testid="quick-pos-btn">
              <Calculator className="w-4 h-4 mr-1.5" /> {t("quickPos")}
            </Button>
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="text-xs font-mono font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
            >
              {lang === "id" ? "ID" : "EN"}
            </button>
            <button
              data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4.5 h-4.5 w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </button>
          </div>
        </header>
        <main className="p-4 md:p-6 max-w-[1400px]">
          <Outlet context={{ outlet }} />
        </main>
      </div>
    </div>
  );
}
